/** HTTP helpers for the Google APIs. Separate from the multi-device `sync/http.ts`
 *  because Google's token endpoint is form-urlencoded and its error envelope
 *  differs ({ error, error_description } for OAuth, { error: { message } } for the
 *  Calendar API). Mirrors `SyncError` with a status-carrying `GoogleSyncError`. */

export class GoogleSyncError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "GoogleSyncError";
  }
}

/** Surface the most specific message Google returned for a failed request. */
function parseError(detail: string): string {
  try {
    const obj = JSON.parse(detail) as {
      error?: unknown;
      error_description?: unknown;
      message?: unknown;
    };
    // Calendar API: { error: { message, code } }
    if (obj.error && typeof obj.error === "object") {
      const m = (obj.error as { message?: unknown }).message;
      if (typeof m === "string") return m;
    }
    // OAuth token endpoint: { error, error_description }
    if (typeof obj.error_description === "string") return obj.error_description;
    if (typeof obj.error === "string") return obj.error;
    if (typeof obj.message === "string") return obj.message;
  } catch {
    /* not JSON */
  }
  return detail;
}

async function fail(res: Response): Promise<never> {
  const detail = await res.text().catch(() => "");
  throw new GoogleSyncError(parseError(detail) || `Google returned ${res.status}`, res.status);
}

/** POST application/x-www-form-urlencoded (the OAuth token + revoke endpoints). */
export async function postForm<T>(url: string, fields: Record<string, string>): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(fields).toString(),
    });
  } catch (e) {
    throw new GoogleSyncError(`Can't reach Google: ${(e as Error).message}`);
  }
  if (!res.ok) await fail(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface FetchOptions {
  method?: string;
  body?: unknown;
  /** Extra headers (e.g. If-Match for conditional update/delete). */
  headers?: Record<string, string>;
}

/** Bearer-authenticated JSON fetch against a Google API. Throws GoogleSyncError
 *  (carrying the HTTP status, so callers can branch on 401/410/412). A 204/empty
 *  body resolves to `undefined`. */
export async function googleFetch<T>(
  url: string,
  accessToken: string,
  opts: FetchOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { authorization: `Bearer ${accessToken}`, ...opts.headers };
  const init: RequestInit = { method: opts.method ?? "GET" };
  if (opts.body != null) {
    headers["content-type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }
  init.headers = headers;
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new GoogleSyncError(`Can't reach Google: ${(e as Error).message}`);
  }
  if (!res.ok) await fail(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const trimSlash = (s: string): string => s.replace(/\/$/, "");
